import React, { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import { User, Notification } from '../../types';

const APK_DOWNLOAD_URL = 'https://github.com/Amr1977/matrix-delivery/releases/latest/download/app-debug.apk';

interface SideMenuProps {
    isOpen: boolean;
    onClose: () => void;
    currentUser: User | null;
    notifications: Notification[];
    onNavigate: (view: string) => void;
    onLogout: () => void;
    onToggleOnline?: () => void;
    isDriverOnline?: boolean;
    onSwitchRole?: (primary_role: string) => void;
    availableRoles?: string[];
    onChangeLocale?: (locale: string) => void;
    currentLocale?: string;
    t: (key: string) => string;
    onSupportClick?: () => void;
    onContactClick?: () => void;
}

const SideMenu: React.FC<SideMenuProps> = ({
    isOpen,
    onClose,
    currentUser,
    notifications,
    onNavigate,
    onLogout,
    onToggleOnline,
    isDriverOnline,
    onSwitchRole,
    availableRoles = [],
    onChangeLocale,
    currentLocale,
    t,
    onSupportClick,
    onContactClick
}) => {
    const unreadCount = notifications.filter(n => !n.isRead).length;
    const { handleSendEmailVerification, loading: sending, error: authError } = useAuth();
    const [resent, setResent] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    // Persist dismissal per-user in localStorage
    useEffect(() => {
        const uid = (currentUser as any)?.id || (currentUser as any)?.email || 'anon';
        const key = `dismiss_verif_${uid}`;
        try {
            const v = localStorage.getItem(key);
            setDismissed(v === '1');
        } catch (e) {
            setDismissed(false);
        }
    }, [currentUser]);

    const handleResend = async () => {
        try {
            const ok = await handleSendEmailVerification();
            if (ok) {
                setResent(true);
                setTimeout(() => setResent(false), 5000);
            }
        } catch (e) {
            // swallow — useAuth surfaces errors via hook state
        }
    };

    const handleDismiss = () => {
        const uid = (currentUser as any)?.id || (currentUser as any)?.email || 'anon';
        const key = `dismiss_verif_${uid}`;
        try { localStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
        setDismissed(true);
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`mobile-menu-backdrop ${isOpen ? 'open' : ''}`}
                onClick={onClose}
                data-testid="menu-backdrop"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 2000,
                    opacity: isOpen ? 1 : 0,
                    visibility: isOpen ? 'visible' : 'hidden',
                    transition: 'opacity 0.3s ease, visibility 0.3s ease'
                }}
            />

            {/* Drawer */}
            <nav
                className={`mobile-menu ${isOpen ? 'open' : ''}`}
                data-testid="side-menu-drawer"
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '300px',
                    background: '#111827',
                    zIndex: 2001,
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s ease-in-out',
                    boxShadow: '-4px 0 15px rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '1px solid var(--matrix-border)'
                }}
            >
                <div className="mobile-menu-items" style={{ padding: '1.5rem', overflowY: 'auto', height: '100%' }}>

                    {/* User Info Section - Enhanced */}
                    <div className="mobile-menu-section" style={{ marginBottom: '2rem', borderBottom: '1px solid #374151', paddingBottom: '1rem' }}>
                        <div
                            className="mobile-user-info"
                            style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}
                        >
                            <div
                                style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '50%',
                                    background: '#374151',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    border: '2px solid var(--matrix-bright-green)',
                                    flexShrink: 0,
                                    transition: 'transform 0.2s',
                                    cursor: 'pointer'
                                }}
                                onClick={() => { onNavigate('profile'); onClose(); }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                                {currentUser?.profile_picture_url ? (
                                    <img src={currentUser.profile_picture_url.startsWith('/') ? `${(process.env.REACT_APP_API_URL || 'https://matrix-api.oldantique50.com/api')}${currentUser.profile_picture_url}` : currentUser.profile_picture_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <img
                                        src={currentUser?.gender === 'female'
                                            ? '/assets/avatars/female_avatar_matrix.png'
                                            : '/assets/avatars/male_avatar_matrix.png'
                                        }
                                        alt="Default Avatar"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                    <div className="mobile-user-name" style={{ fontWeight: 'bold', color: 'white', fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {currentUser?.name}
                                    </div>
                                    {currentUser?.is_verified && (
                                        <span style={{
                                            background: '#10B981',
                                            color: 'white',
                                            padding: '0.125rem 0.375rem',
                                            borderRadius: '9999px',
                                            fontSize: '0.625rem',
                                            fontWeight: '700',
                                            flexShrink: 0
                                        }}>
                                            ✓
                                        </span>
                                    )}
                                </div>
                                <div className="mobile-user-primary_role" style={{ color: 'var(--matrix-green)', fontSize: '0.75rem', textTransform: 'capitalize', marginBottom: '0.5rem' }}>
                                    {currentUser?.primary_role || currentUser?.primary_role}
                                </div>
                                {/* User Stats */}
                                {currentUser?.primary_role === 'driver' && (
                                    <div style={{ fontSize: '0.75rem', color: '#9CA3AF', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                        {currentUser?.completedDeliveries !== undefined && (
                                            <div>
                                                🚚 {currentUser.completedDeliveries} {currentUser.completedDeliveries === 1 ? t('menu.delivery') : t('menu.deliveries')}
                                            </div>
                                        )}
                                        {currentUser?.rating !== undefined && currentUser.rating > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                <span style={{ color: '#FFD700' }}>
                                                    {'★'.repeat(Math.floor(currentUser.rating))}
                                                    {currentUser.rating % 1 >= 0.5 ? '½' : ''}
                                                    {'☆'.repeat(5 - Math.ceil(currentUser.rating))}
                                                </span>
                                                <span style={{ color: '#D1D5DB', fontSize: '0.7rem' }}>
                                                    {currentUser.rating.toFixed(1)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Email verification warning (matrix-styled) */}
                        {currentUser && !currentUser.is_verified && !dismissed && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                borderRadius: '0.5rem',
                                background: 'linear-gradient(180deg, rgba(2,6,5,0.55), rgba(4,10,8,0.85))',
                                border: '1px solid rgba(36,190,121,0.18)',
                                boxShadow: '0 8px 30px rgba(16,185,129,0.06), inset 0 1px 0 rgba(255,255,255,0.02)',
                                color: 'var(--matrix-bright-green)',
                                position: 'relative',
                                fontFamily: 'monospace'
                            }}>
                                <button onClick={handleDismiss} aria-label="Dismiss verification" style={{ position: 'absolute', right: 8, top: 8, background: 'transparent', border: 'none', color: 'rgba(36,190,121,0.8)', fontSize: '1rem', cursor: 'pointer' }}>×</button>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, color: 'var(--matrix-bright-green)', marginBottom: 4, wordBreak: 'break-word' }}>✉️ {t('auth.verifyYourEmail')}</div>
                                        <div style={{ fontSize: '0.85rem', color: 'rgba(167,243,208,0.95)', wordBreak: 'break-word' }}>{t('auth.emailVerificationRequired')}</div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <button onClick={handleResend} disabled={sending} style={{ width: '100%', background: 'transparent', border: '1px solid rgba(36,190,121,0.12)', color: 'var(--matrix-bright-green)', padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}>{sending ? t('auth.sending') : (resent ? t('auth.resent') : t('auth.resendVerification'))}</button>
                                        <button onClick={() => { onNavigate('profile'); onClose(); }} style={{ width: '100%', background: 'linear-gradient(90deg,#24be79,#10b981)', border: 'none', color: '#041014', padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}>{t('auth.verifyNow') || 'Verify'}</button>
                                    </div>
                                </div>
                                {authError && <div style={{ marginTop: 8, color: '#FCA5A5', fontSize: '0.8rem' }}>{authError}</div>}
                            </div>
                        )}
                    </div>

                    {/* Navigation Links */}
                    <div className="mobile-menu-section" style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '1rem', letterSpacing: '0.05em' }}>{t('menu.menuTitle')}</h4>

                        <button
                            onClick={() => { onNavigate('home'); onClose(); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            🏠 {t('menu.homeActiveOrders')}
                        </button>

                        <button
                            onClick={() => { onNavigate('marketplace'); onClose(); }}
                            data-testid="marketplace-menu-btn"
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            🛍️ {t('marketplace.browseStores')}
                        </button>

                        {currentUser?.primary_role === 'vendor' && (
                            <button
                                onClick={() => { onNavigate('vendor_dashboard'); onClose(); }}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.05) 100%)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#10B981', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'all 0.2s', fontWeight: '600' }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)'; e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.05) 100%)'; e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)'; }}
                            >
                                🏪 {t('menu.vendorDashboard') || 'Vendor Dashboard'}
                            </button>
                        )}

                        {currentUser?.primary_role === 'driver' && (
                            <button
                                onClick={() => { onNavigate('earnings'); onClose(); }}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                💰 {t('menu.earningsDashboard')}
                            </button>
                        )}

                        <button
                            onClick={() => { onNavigate('notifications'); onClose(); }}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <span>🔔 {t('menu.notifications')}</span>
                            {unreadCount > 0 && (
                                <span style={{ background: '#EF4444', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                    {unreadCount}
                                </span>
                            )}
                        </button>

                        <button
                            onClick={() => { window.location.href = '/balance'; onClose(); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            💰 {t('menu.balance')}
                        </button>

                        <button
                            onClick={() => { onNavigate('history'); onClose(); }}
                            data-testid="history-menu-btn"
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            📜 {t('menu.myHistory')}
                        </button>

                        <button
                            onClick={() => { onNavigate('settings'); onClose(); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            ⚙️ {t('menu.settings')}
                        </button>

                        <button
                            onClick={() => { window.location.href = '/couriers/directory'; onClose(); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            👥 {t('courier.directory.title')}
                        </button>

                        <button
                            onClick={() => { window.location.href = '/downloads'; onClose(); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            ⬇️ Downloads
                        </button>

                        {/* APK Download Section */}
                        <div style={{ marginTop: '1rem', padding: '1rem', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <span style={{ fontSize: '1.25rem' }}>📱</span>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 'bold', color: 'white', fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {t('menu.downloadApp') || 'Download Android App'}
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#9CA3AF', marginTop: '0.125rem' }}>
                                        {t('menu.downloadAppDesc') || 'Get the latest APK from GitHub releases'}
                                    </div>
                                </div>
                            </div>
                            <a
                                href={APK_DOWNLOAD_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'center',
                                    padding: '0.625rem',
                                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    borderRadius: '0.5rem',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                                }}
                            >
                                ⬇️ {t('menu.downloadApk') || 'Download Latest APK'}
                            </a>
                        </div>

                        {/* Admin Panel - Only for admins */}
                        {(currentUser?.primary_role === 'admin' || (currentUser?.granted_roles && currentUser.granted_roles.includes('admin')) || availableRoles.includes('admin')) && (
                            <button
                                onClick={() => { onNavigate('admin_panel'); onClose(); }}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: '0.75rem',
                                    background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.2) 0%, rgba(99, 102, 241, 0.1) 100%)',
                                    border: '1px solid rgba(99, 102, 241, 0.3)',
                                    color: '#A5B4FC',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    borderRadius: '0.5rem',
                                    marginBottom: '0.5rem',
                                    transition: 'all 0.2s',
                                    fontWeight: '600'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(79, 70, 229, 0.3) 0%, rgba(99, 102, 241, 0.2) 100%)';
                                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(79, 70, 229, 0.2) 0%, rgba(99, 102, 241, 0.1) 100%)';
                                    e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                                }}
                            >
                                🛡️ {t('menu.adminPanel')}
                            </button>
                        )}
                    </div>

                    {/* Driver Actions */}
                    {currentUser?.primary_role === 'driver' && (
                        <div className="mobile-menu-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '1rem', letterSpacing: '0.05em' }}>{t('menu.driverActions')}</h4>

                            <button
                                onClick={() => { onNavigate('bidding'); onClose(); }}
                                data-testid="bidding-menu-btn"
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                📋 {t('menu.availableBids')}
                            </button>

                            <button
                                onClick={() => { onNavigate('map'); onClose(); }}
                                data-testid="map-menu-btn"
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                🗺️ {t('menu.ordersMap')}
                            </button>

                            <button
                                onClick={() => { onNavigate('my_bids'); onClose(); }}
                                data-testid="my-bids-menu-btn"
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                🎯 {t('menu.myBids')}
                            </button>

                            <button
                                onClick={() => { onNavigate('location_settings'); onClose(); }}
                                data-testid="location-menu-btn"
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }}
                                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                📍 {t('menu.location')}
                            </button>
                        </div>
                    )}

                    {/* Driver Controls */}
                    {currentUser?.primary_role === 'driver' && onToggleOnline && (
                        <div className="mobile-menu-section" style={{ marginBottom: '2rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '0.5rem' }}>
                            <h4 style={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '1rem', letterSpacing: '0.05em' }}>{t('menu.driverStatus')}</h4>
                            <button
                                onClick={onToggleOnline}
                                data-testid="toggle-online-btn"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    borderRadius: '0.5rem',
                                    border: 'none',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    background: isDriverOnline ? '#EF4444' : '#10B981',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                {isDriverOnline ? `🔴 ${t('menu.goOffline')}` : `🟢 ${t('menu.goOnline')}`}
                            </button>
                        </div>
                    )}

                    {/* Legal Section */}
                    <div className="mobile-menu-section" style={{ marginTop: '2rem' }}>
                        <h4 style={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '1rem', letterSpacing: '0.05em' }}>{t('menu.legal')}</h4>
                        <button onClick={() => { onNavigate('legal_privacy'); onClose(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                            🔒 {t('menu.privacyPolicy')}
                        </button>
                        <button onClick={() => { onNavigate('legal_terms'); onClose(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                            📜 {t('menu.termsOfService')}
                        </button>
                        <button onClick={() => { onNavigate('legal_refund'); onClose(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                            💸 {t('menu.refundPolicy')}
                        </button>
                        <button onClick={() => { onNavigate('legal_driver_agreement'); onClose(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                            🤝 {t('menu.driverAgreement')}
                        </button>
                        <button onClick={() => { onNavigate('legal_cookies'); onClose(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s' }} onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                            🍪 {t('menu.cookiePolicy')}
                        </button>
                    </div>

                    {/* Settings & System */}
                    <div className="mobile-menu-section" style={{ marginTop: '2rem' }}>
                        <h4 style={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '1rem', letterSpacing: '0.05em' }}>{t('menu.system')}</h4>


                        <button
                            onClick={() => { if (onContactClick) onContactClick(); onClose(); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s', fontWeight: 'bold' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            📞 {t('menu.contactUs')}
                        </button>

                        {/* Crypto Test - Only in development/staging */}
                        {process.env.REACT_APP_ENV !== 'production' && (
                            <button
                                onClick={() => { onNavigate('crypto-test'); onClose(); }}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: '0.75rem',
                                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                    border: '1px solid rgba(102, 126, 234, 0.3)',
                                    color: '#A78BFA',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    borderRadius: '0.5rem',
                                    marginBottom: '0.5rem',
                                    transition: 'all 0.2s',
                                    fontWeight: '600'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.2) 100%)';
                                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.1) 100%)';
                                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                                }}
                            >
                                🧪 {t('menu.cryptoTest')}
                            </button>
                        )}

                        <button
                            onClick={() => { if (onSupportClick) onSupportClick(); onClose(); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.75rem', background: 'transparent', border: 'none', color: '#10B981', cursor: 'pointer', fontSize: '1rem', borderRadius: '0.5rem', marginBottom: '0.5rem', transition: 'background 0.2s', fontWeight: 'bold' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            ☕ {t('menu.supportDeveloper')}
                        </button>





                        <button
                            onClick={onLogout}
                            className="btn-danger"
                            data-testid="logout-menu-btn"
                            style={{ width: '100%', marginTop: '1rem' }}
                        >
                            {t('auth.logout')}
                        </button>
                    </div>

                </div>
            </nav>
        </>
    );
};

export default SideMenu;
